import { getCartLines, getMaticCount } from "@/cart/displayCart";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";
import { isMaticCare } from "@/utils/ShopifyHelpers";
import { PET_BRUSH_ROLL_VARIANT_ID } from "@/consts/shopify";

// Per-Matic add-ons can never exceed the number of Matics in the order.
export const syncPerMaticAddOnsToMatics = async (cart: Cart): Promise<Cart> => {
  const maticCount = getMaticCount(cart);
  const lines = getCartLines(cart);
  const addOnGroups = [
    lines.filter(isMaticCare),
    lines.filter((line) => line.merchandise.id === PET_BRUSH_ROLL_VARIANT_ID),
  ];
  const lineUpdates: Array<{ id: string; quantity: number }> = [];
  const lineIdsToRemove: string[] = [];

  for (const addOnLines of addOnGroups) {
    let allowed = maticCount;

    for (const line of addOnLines) {
      const nextQuantity = Math.min(line.quantity, allowed);
      allowed -= nextQuantity;
      if (nextQuantity === 0) {
        lineIdsToRemove.push(line.id);
      } else if (nextQuantity !== line.quantity) {
        lineUpdates.push({ id: line.id, quantity: nextQuantity });
      }
    }
  }

  let nextCart = cart;

  if (lineUpdates.length > 0) {
    const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
      cartId: nextCart.id,
      lines: lineUpdates,
    });
    const updatedCart = cartLinesUpdate?.cart as Cart | undefined;
    if (!updatedCart) {
      throw new Error(
        "syncPerMaticAddOnsToMatics: trimming add-on lines failed",
      );
    }
    nextCart = updatedCart;
  }

  if (lineIdsToRemove.length > 0) {
    const { cartLinesRemove } = await GRAPHQL_CLIENT.RemoveFromCart({
      cartId: nextCart.id,
      lineIds: lineIdsToRemove,
    });
    const trimmedCart = cartLinesRemove?.cart as Cart | undefined;
    if (!trimmedCart) {
      throw new Error(
        "syncPerMaticAddOnsToMatics: removing add-on lines failed",
      );
    }
    nextCart = trimmedCart;
  }

  return nextCart;
};
